import React, { useEffect, useState, useRef, useCallback } from "react";
import Sidebar from "components/Sidebar/Sidebar.js";
import config from "services/config";
import axios from "axios";
import {
  Play,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  Users,
  Building2,
  FileText,
  Zap,
  Eye,
  HardDrive,
  Send,
  Square,
  Trash2,
  History,
  ChevronDown,
  Calendar,
  Hash,
} from "lucide-react";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e8ecf1",
  overflow: "hidden",
};

export function IcontadorView() {
  const [status, setStatus] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [logs, setLogs] = useState([]);
  const [rpaConfig, setRpaConfig] = useState(null);
  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);

  // Historial
  const [logFiles, setLogFiles] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logData, setLogData] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);

  useEffect(() => {
    axios.get(`${config.url}/icontador/config`).then(({ data }) => setRpaConfig(data)).catch(() => {});
    axios.get(`${config.url}/icontador/logs`).then(({ data }) => setLogFiles(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedLog) { setLogData(null); return; }
    setLoadingLog(true);
    axios.get(`${config.url}/icontador/logs/${selectedLog.file}`)
      .then(({ data }) => setLogData(data))
      .catch(() => setLogData(null))
      .finally(() => setLoadingLog(false));
  }, [selectedLog]);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.url}/icontador/status`);
      setStatus(data);
    } catch {
      setStatus({ isRunning: false, progress: null });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // SSE connection when running
  useEffect(() => {
    if (!status?.isRunning) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    const es = new EventSource(`${config.url}/icontador/stream`);
    eventSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setStatus((prev) => ({ ...prev, progress: data }));

        if (data.currentClient) {
          setLogs((prev) => {
            const last = prev[prev.length - 1];
            if (last?.client === data.currentClient) {
              return prev.map((l, i) =>
                i === prev.length - 1
                  ? { ...l, steps: data.clientSteps || [] }
                  : l
              );
            }
            const updated = prev.map((l) => ({ ...l, active: false }));
            return [
              ...updated,
              {
                time: new Date().toLocaleTimeString(),
                client: data.currentClient,
                index: data.currentIndex,
                total: data.total,
                steps: data.clientSteps || [],
                active: true,
              },
            ];
          });
        }

        // Marcar error en el log del cliente
        if (data.clientError) {
          setLogs((prev) =>
            prev.map((l) =>
              l.client === data.clientError.client
                ? { ...l, active: false, error: data.clientError.error }
                : l
            )
          );
        }

        // Marcar skipped
        if (data.clientSkipped) {
          setLogs((prev) =>
            prev.map((l) =>
              l.client === data.clientSkipped.client
                ? { ...l, active: false, skipped: true }
                : l
            )
          );
        }

        if (data.done) {
          setLogs((prev) => prev.map((l) => ({ ...l, active: false })));
          es.close();
          eventSourceRef.current = null;
          fetchStatus();
        }
      } catch {}
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
    };

    return () => es.close();
  }, [status?.isRunning, fetchStatus]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const handleRun = async (mode = "real") => {
    // mode: "dryRun" | "skipEmail" | "real"
    const dryRun = mode === "dryRun";
    const skipEmail = mode === "skipEmail";
    if (mode === "real" && !window.confirm("Esto ejecutara el RPA en modo REAL: descargara PDFs, subira archivos a Google Drive y enviara emails a los clientes.\n\nDeseas continuar?")) return;
    if (mode === "skipEmail" && !window.confirm("Modo SIN EMAIL: descarga PDFs y los sube a Google Drive, pero NO envia email a los clientes. Util para backfill.\n\nDeseas continuar?")) return;
    setLaunching(true);
    setLogs([]);
    try {
      await axios.post(`${config.url}/icontador/run`, { dryRun, skipEmail, maxRetries: 5 });
      setTimeout(fetchStatus, 1000);
    } catch (err) {
      if (err.response?.status === 409) {
        alert("Ya hay un proceso en ejecucion");
      } else {
        alert("Error al iniciar: " + (err.response?.data?.error || err.message));
      }
    } finally {
      setLaunching(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm("Detener el proceso? Se guardara el progreso actual.")) return;
    try {
      await axios.post(`${config.url}/icontador/stop`);
      setTimeout(fetchStatus, 1500);
    } catch (err) {
      alert("Error al detener: " + (err.response?.data?.error || err.message));
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Limpiar estado? Se borraran los archivos pending y completed, y se regenerara el master desde la base de datos.")) return;
    try {
      await axios.post(`${config.url}/icontador/reset`);
      setLogs([]);
      fetchStatus();
      alert("Estado limpiado y master regenerado");
    } catch (err) {
      alert("Error: " + (err.response?.data?.error || err.message));
    }
  };

  const p = status?.progress;
  const isRunning = status?.isRunning;
  const isDryRun = p?.dryRun;
  const progressPct =
    p?.total > 0 ? Math.round(((p.completed || 0) / p.total) * 100) : 0;

  const statusLabel = {
    syncing: "Sincronizando DB...",
    starting: "Iniciando...",
    running: "Procesando",
    retrying: "Reintentando...",
    completed: "Completado",
    exhausted: "Agoto reintentos",
    stopping: "Deteniendo...",
    stopped: "Detenido",
  };

  const statusColor = {
    syncing: "#7c3aed",
    starting: "#d97706",
    running: "#0353a4",
    retrying: "#d97706",
    completed: "#16a34a",
    exhausted: "#dc2626",
    stopping: "#dc2626",
    stopped: "#dc2626",
  };

  return (
    <>
      <Sidebar />
      <div
        style={{
          marginLeft: 210,
          minHeight: "100vh",
          background: "#fff",
          padding: "24px 32px 32px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Zap size={22} color="#0353a4" />
            <h1
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
              }}
            >
              iContador RPA
            </h1>
            {rpaConfig && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#0353a4",
                  background: "#eff6ff",
                  padding: "3px 10px",
                  borderRadius: 20,
                  border: "1px solid #dbeafe",
                }}
              >
                {rpaConfig.month} {rpaConfig.year}
              </span>
            )}
            {isDryRun && isRunning && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#d97706",
                  background: "#fef3c7",
                  padding: "2px 8px",
                  borderRadius: 20,
                  letterSpacing: "0.05em",
                }}
              >
                DRY RUN
              </span>
            )}
            {isRunning && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: statusColor[p?.status] || "#0353a4",
                  background: `${statusColor[p?.status] || "#0353a4"}14`,
                  padding: "3px 10px",
                  borderRadius: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: statusColor[p?.status] || "#0353a4",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                {statusLabel[p?.status] || p?.status}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!isRunning && (
              <button
                onClick={handleReset}
                disabled={launching}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "1px solid #fca5a5",
                  background: "#fff",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <Trash2 size={14} />
                Limpiar
              </button>
            )}
            <button
              onClick={() => handleRun("dryRun")}
              disabled={isRunning || launching}
              title="No toca Drive, no manda email — solo simula"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: isRunning ? "#94a3b8" : "#334155",
                fontSize: 13,
                fontWeight: 500,
                cursor: isRunning ? "not-allowed" : "pointer",
                transition: "all 0.15s",
                opacity: isRunning ? 0.5 : 1,
              }}
            >
              <Eye size={14} />
              DRY RUN
            </button>
            <button
              onClick={() => handleRun("skipEmail")}
              disabled={isRunning || launching}
              title="Sube PDFs a Drive pero NO envia email — para backfill"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #fcd34d",
                background: "#fffbeb",
                color: isRunning ? "#94a3b8" : "#92400e",
                fontSize: 13,
                fontWeight: 500,
                cursor: isRunning ? "not-allowed" : "pointer",
                transition: "all 0.15s",
                opacity: isRunning ? 0.5 : 1,
              }}
            >
              <Play size={14} />
              SIN EMAIL
            </button>
            {isRunning ? (
              <button
                onClick={handleStop}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: "#dc2626",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <Square size={14} />
                Detener
              </button>
            ) : (
              <button
                onClick={() => handleRun("real")}
                disabled={launching}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: "#0353a4",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: launching ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                }}
              >
                <Play size={14} />
                Ejecutar RPA
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        {p && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <StatCard
              label="Total Clientes"
              value={p.total || "\u2014"}
              icon={Building2}
              accent="#0353a4"
            />
            <StatCard
              label="Completados"
              value={p.completed || 0}
              icon={CheckCircle}
              accent="#16a34a"
            />
            <StatCard
              label="Pendientes"
              value={p.pending || 0}
              icon={Clock}
              accent="#d97706"
            />
            <StatCard
              label="Intento"
              value={p.attempt ? `${p.attempt}/5` : "\u2014"}
              icon={RefreshCw}
              accent="#7c3aed"
            />
          </div>
        )}

        {/* Progress Bar */}
        {isRunning && p?.total > 0 && (
          <div style={{ ...CARD, padding: "16px 20px", marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FileText size={15} color="#0353a4" />
                <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                  {p.currentClient || "Iniciando..."}
                </span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#0353a4" }}>
                {progressPct}%
              </span>
            </div>
            <div
              style={{
                width: "100%",
                height: 8,
                background: "#f1f5f9",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${progressPct}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #0353a4, #1d4ed8)",
                  borderRadius: 4,
                  transition: "width 0.5s ease",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
              }}
            >
              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                {p.completed || 0} de {p.total} clientes
              </span>
              {p.lastAttemptFailed > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <AlertCircle size={11} />
                  {p.lastAttemptFailed} errores
                </span>
              )}
            </div>
          </div>
        )}

        {/* Completed state */}
        {!isRunning && p?.status === "completed" && (
          <div
            style={{
              ...CARD,
              padding: "32px",
              marginBottom: 20,
              textAlign: "center",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
            }}
          >
            <CheckCircle size={40} color="#16a34a" style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 16, fontWeight: 600, color: "#166534", margin: 0 }}>
              Proceso completado
            </p>
            <p style={{ fontSize: 13, color: "#4ade80", margin: "4px 0 0" }}>
              {p.completed} clientes procesados exitosamente
            </p>
          </div>
        )}

        {/* Live Log */}
        <div style={{ ...CARD }}>
          <div
            style={{
              padding: "14px 20px",
              borderBottom: "1px solid #e8ecf1",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={15} color="#0353a4" />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                Actividad
              </span>
              {logs.length > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#64748b",
                    background: "#f1f5f9",
                    padding: "2px 8px",
                    borderRadius: 20,
                  }}
                >
                  {logs.length}
                </span>
              )}
            </div>
            {logs.length > 0 && (
              <button
                onClick={() => setLogs([])}
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Limpiar
              </button>
            )}
          </div>
          <div
            style={{
              maxHeight: 500,
              overflowY: "auto",
              padding: logs.length === 0 ? "40px 20px" : 0,
            }}
          >
            {logs.length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: 14,
                  margin: 0,
                }}
              >
                {isRunning
                  ? "Esperando actividad..."
                  : "Ejecuta el RPA para ver la actividad"}
              </p>
            ) : (
              <div>
                {logs.map((log, i) => (
                  <LogEntry
                    key={`${log.client}-${i}`}
                    log={log}
                    isLast={i === logs.length - 1}
                    isRunning={isRunning}
                    isDryRun={isDryRun}
                  />
                ))}
              </div>
            )}
            <div ref={logsEndRef} />
          </div>
        </div>

        {/* ─── Historial de Reportes ─── */}
        <div style={{ ...CARD, marginTop: 20 }}>
          <div
            style={{
              padding: "14px 20px",
              borderBottom: "1px solid #e8ecf1",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <History size={15} color="#0353a4" />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                Historial de Reportes
              </span>
            </div>
            <div style={{ position: "relative" }}>
              <select
                value={selectedLog ? selectedLog.file : ""}
                onChange={(e) => {
                  const f = logFiles.find((l) => l.file === e.target.value);
                  setSelectedLog(f || null);
                }}
                style={{
                  appearance: "none",
                  padding: "6px 32px 6px 12px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#334155",
                  cursor: "pointer",
                  minWidth: 180,
                }}
              >
                <option value="">Seleccionar mes...</option>
                {logFiles.map((l) => (
                  <option key={l.file} value={l.file}>
                    {l.month} {l.year}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                color="#94a3b8"
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
              />
            </div>
          </div>

          <div style={{ padding: selectedLog ? 0 : "40px 20px" }}>
            {!selectedLog ? (
              <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 14, margin: 0 }}>
                Selecciona un mes para ver el reporte
              </p>
            ) : loadingLog ? (
              <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 14, margin: 0, padding: "40px 20px" }}>
                Cargando...
              </p>
            ) : logData && logData.length > 0 ? (
              logData.map((entry, ei) => (
                <ReportEntry key={ei} entry={entry} index={ei} total={logData.length} />
              ))
            ) : (
              <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 14, margin: 0, padding: "40px 20px" }}>
                Sin datos para este mes
              </p>
            )}
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.5); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes slideRight {
            from { opacity: 0; transform: translateX(-12px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}</style>
      </div>
    </>
  );
}

/* ─── Log Entry (per client in activity list) ─── */
function LogEntry({ log, isLast, isRunning, isDryRun }) {
  const pdfs = (log.steps || []).filter((s) => s.type === "pdf_downloaded");
  const uploads = (log.steps || []).filter((s) => s.type === "drive_uploaded");
  const emails = (log.steps || []).filter((s) => s.type === "email_sent");
  const workersInfo = (log.steps || []).find((s) => s.type === "workers_found");
  const isActive = log.active && isRunning;
  const hasError = !!log.error;
  const isSkipped = !!log.skipped;
  const hasSteps = pdfs.length > 0 || uploads.length > 0 || emails.length > 0;

  const rowBg = hasError
    ? "#fef2f2"
    : isSkipped
    ? "#fffbeb"
    : isActive
    ? "#f8fbff"
    : "transparent";

  return (
    <div
      style={{
        padding: "14px 20px",
        borderBottom: "1px solid #f1f5f9",
        background: rowBg,
        transition: "background 0.3s ease",
        borderLeft: hasError ? "3px solid #dc2626" : isSkipped ? "3px solid #d97706" : "3px solid transparent",
      }}
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", minWidth: 24 }}>
          {log.index}
        </span>
        <span style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace", minWidth: 70 }}>
          {log.time}
        </span>
        <span style={{
          fontSize: 13,
          fontWeight: 600,
          color: hasError ? "#dc2626" : isSkipped ? "#92400e" : "#0f172a",
          flex: 1,
        }}>
          {log.client}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {hasError && <Badge color="#dc2626" bg="#fee2e2" border="#fca5a5" icon={AlertCircle} label="Error" />}
          {isSkipped && <Badge color="#92400e" bg="#fef3c7" border="#fde68a" icon={AlertCircle} label="Skipped" />}
          {!hasError && !isSkipped && pdfs.length > 0 && (
            <Badge color="#16a34a" bg="#f0fdf4" border="#bbf7d0" icon={CheckCircle} label="OK" />
          )}
          <span style={{ fontSize: 12, fontWeight: 500, color: "#0353a4", minWidth: 40, textAlign: "right" }}>
            {log.index}/{log.total}
          </span>
        </div>
      </div>

      {/* Error message */}
      {hasError && (
        <div style={{
          marginTop: 8,
          marginLeft: 106,
          fontSize: 11,
          color: "#dc2626",
          background: "#fee2e2",
          padding: "6px 10px",
          borderRadius: 6,
          fontFamily: "monospace",
          animation: "fadeInUp 0.2s ease",
        }}>
          {log.error}
        </div>
      )}

      {/* Steps detail — always visible when steps exist */}
      {hasSteps && (
        <div style={{ marginTop: 10, marginLeft: 106 }}>
          {/* PDFs row */}
          {pdfs.length > 0 && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                fontSize: 10, fontWeight: 600, color: "#1e40af",
                minWidth: 70, paddingTop: 2,
              }}>
                <FileText size={11} color="#3b82f6" />
                {pdfs.length} PDF{pdfs.length > 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {pdfs.map((pdf, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 10,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "#eff6ff",
                      border: "1px solid #dbeafe",
                      color: "#1e40af",
                      fontWeight: 500,
                      animation: isActive ? "fadeInUp 0.2s ease forwards" : "none",
                      animationDelay: isActive ? `${i * 0.05}s` : "0s",
                    }}
                  >
                    {pdf.worker}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Drive uploads row */}
          {uploads.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                fontSize: 10, fontWeight: 600,
                color: uploads[0]?.simulated ? "#92400e" : "#166534",
                minWidth: 70,
              }}>
                <HardDrive size={11} color={uploads[0]?.simulated ? "#d97706" : "#16a34a"} />
                Drive
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "3px 10px",
                borderRadius: 6,
                background: uploads[0]?.simulated ? "#fffbeb" : "#f0fdf4",
                border: `1px solid ${uploads[0]?.simulated ? "#fde68a" : "#bbf7d0"}`,
                fontSize: 10, fontWeight: 500,
                color: uploads[0]?.simulated ? "#92400e" : "#166534",
                animation: isActive ? "scaleIn 0.3s ease" : "none",
              }}>
                <CheckCircle size={10} color={uploads[0]?.simulated ? "#d97706" : "#16a34a"} />
                {uploads.length} archivo{uploads.length > 1 ? "s" : ""} subido{uploads.length > 1 ? "s" : ""}
                {uploads[0]?.simulated && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: "#d97706",
                    background: "#fef3c7", padding: "0 5px", borderRadius: 8,
                    marginLeft: 4,
                  }}>
                    SIMULATED
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Email row */}
          {emails.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                fontSize: 10, fontWeight: 600,
                color: emails[0]?.simulated ? "#92400e" : "#5b21b6",
                minWidth: 70,
              }}>
                <Send size={11} color={emails[0]?.simulated ? "#d97706" : "#7c3aed"} />
                Email
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "3px 10px",
                borderRadius: 6,
                background: emails[0]?.simulated ? "#fffbeb" : "#f5f3ff",
                border: `1px solid ${emails[0]?.simulated ? "#fde68a" : "#e9d5ff"}`,
                fontSize: 10, fontWeight: 500,
                color: emails[0]?.simulated ? "#92400e" : "#5b21b6",
                animation: isActive ? "slideRight 0.4s ease" : "none",
              }}>
                <CheckCircle size={10} color={emails[0]?.simulated ? "#d97706" : "#7c3aed"} />
                {emails[0]?.to} ({emails[0]?.attachments} PDFs)
                {emails[0]?.simulated && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: "#d97706",
                    background: "#fef3c7", padding: "0 5px", borderRadius: 8,
                    marginLeft: 4,
                  }}>
                    SIMULATED
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ color, bg, border, icon: Icon, label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 3,
      padding: "2px 8px", borderRadius: 10,
      background: bg, border: `1px solid ${border}`,
      fontSize: 10, fontWeight: 600, color,
    }}>
      <Icon size={10} />
      {label}
    </div>
  );
}

/* ─── Report Entry (historial) ─── */
function ReportEntry({ entry, index, total }) {
  const date = new Date(entry.timestamp);
  const dateStr = date.toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = date.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" });
  const totalWorkers = (entry.summary || []).reduce((sum, c) => sum + (c.workersCount || 0), 0);

  return (
    <div
      style={{
        padding: "16px 20px",
        borderBottom: index < total - 1 ? "1px solid #f1f5f9" : "none",
      }}
    >
      {/* Header de la ejecución */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: "#fff",
            background: "#0353a4", padding: "2px 8px", borderRadius: 6, minWidth: 20, textAlign: "center",
          }}>
            #{index + 1}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Calendar size={12} color="#64748b" />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
              {dateStr}
            </span>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>{timeStr}</span>
          </div>
          {entry.dryRun && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: "#d97706",
              background: "#fef3c7", padding: "2px 8px", borderRadius: 20,
            }}>
              DRY RUN
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Building2 size={12} color="#0353a4" />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#0353a4" }}>
              {entry.clientsProcessed} cliente{entry.clientsProcessed !== 1 ? "s" : ""}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Users size={12} color="#7c3aed" />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#7c3aed" }}>
              {totalWorkers} trabajador{totalWorkers !== 1 ? "es" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Tabla de clientes */}
      {entry.summary && entry.summary.length > 0 && (
        <div style={{
          background: "#f8fafc", borderRadius: 8, border: "1px solid #e8ecf1", overflow: "hidden",
        }}>
          <div style={{
            display: "grid", gridTemplateColumns: "40px 1fr 120px 100px",
            padding: "8px 14px", background: "#f1f5f9",
            fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            <span><Hash size={10} style={{ verticalAlign: "middle" }} /></span>
            <span>Empresa</span>
            <span>RUT</span>
            <span style={{ textAlign: "right" }}>Trabajadores</span>
          </div>
          {entry.summary.map((client, ci) => (
            <div
              key={ci}
              style={{
                display: "grid", gridTemplateColumns: "40px 1fr 120px 100px",
                padding: "8px 14px",
                borderTop: "1px solid #e8ecf1",
                fontSize: 12, color: "#334155",
                background: ci % 2 === 0 ? "#fff" : "#f8fafc",
              }}
            >
              <span style={{ color: "#94a3b8", fontWeight: 500 }}>{ci + 1}</span>
              <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {client.name}
              </span>
              <span style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>
                {client.rut}
              </span>
              <span style={{ textAlign: "right", fontWeight: 600, color: client.workersCount > 0 ? "#0353a4" : "#94a3b8" }}>
                {client.workersCount}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent = "#0353a4" }) {
  return (
    <div style={{ ...CARD, padding: "16px 18px", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          background: accent,
          borderRadius: "12px 0 0 12px",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#8b95a5",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              margin: 0,
              marginBottom: 4,
            }}
          >
            {label}
          </p>
          <p style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0 }}>
            {value}
          </p>
        </div>
        {Icon && (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `${accent}14`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={18} color={accent} />
          </div>
        )}
      </div>
    </div>
  );
}
